import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import StateBadge from "@/components/StateBadge";
import {
  ENDPOINTS,
  CITY_STATS,
  COUNTRIES,
  VENUE_TYPES,
  MEDIA_TYPES,
  ENVIRONMENTS,
} from "@/lib/networkData";
import { MapPin, ArrowRight, Filter } from "lucide-react";

const FILTERS = {
  country: { key: "country", label: "Country", options: COUNTRIES },
  city: { key: "city", label: "City", options: CITY_STATS.map((c) => c.city) },
  venue_type: { key: "venue_type", label: "Venue", options: VENUE_TYPES },
  media_type: { key: "media_type", label: "Media", options: MEDIA_TYPES },
  environment: { key: "environment", label: "Environment", options: ENVIRONMENTS },
};

export default function NetworkExplorer() {
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState(null);

  const toggle = (key, val) => {
    setFilters((f) => {
      const cur = f[key] || [];
      const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
      return { ...f, [key]: next.length ? next : undefined };
    });
  };

  const clear = () => setFilters({});

  const filtered = useMemo(
    () =>
      ENDPOINTS.filter((e) =>
        Object.entries(filters).every(([k, vals]) => !vals || vals.includes(e[k]))
      ),
    [filters]
  );

  return (
    <PublicShell>
      <section className="pt-28 md:pt-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-10">
          <div className="flex items-center gap-3">
            <StateBadge state="DEMO" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
              Network Explorer · signature experience
            </span>
          </div>
          <h1 className="mt-6 font-display font-black tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            Explore the Network.
          </h1>
          <p className="mt-6 max-w-2xl text-white/60 text-lg">
            Every place is context. Every screen is an endpoint. Filter our
            simulated Mirrorloops inventory by country, city, venue, media
            and environment. Live inventory appears here as venues connect.
          </p>
        </div>

        {/* Map */}
        <div className="border-t border-white/10 bg-[#0d0c0b]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8">
            <svg
              viewBox="0 0 1000 500"
              preserveAspectRatio="xMidYMid meet"
              className="w-full h-[300px] md:h-[380px]"
              data-testid="network-map"
            >
              <g stroke="rgba(255,255,255,0.04)" strokeWidth="1">
                {Array.from({ length: 20 }).map((_, i) => (
                  <line key={`v${i}`} x1={i * 50} y1={0} x2={i * 50} y2={500} />
                ))}
                {Array.from({ length: 10 }).map((_, i) => (
                  <line key={`h${i}`} x1={0} y1={i * 50} x2={1000} y2={i * 50} />
                ))}
              </g>
              {filtered.map((e) => (
                <g key={e.id} onClick={() => setSelected(e)} style={{ cursor: "pointer" }}>
                  <circle
                    cx={e.x}
                    cy={e.y}
                    r={selected?.id === e.id ? 6 : 3}
                    fill={e.availability === "booked" ? "#00E5FF" : "#E8D6A3"}
                  >
                    <animate
                      attributeName="opacity"
                      values="1;0.4;1"
                      dur="3s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </g>
              ))}
              {CITY_STATS.map((c) => (
                <text
                  key={c.city}
                  x={c.x + 10}
                  y={c.y + 4}
                  fill="rgba(255,255,255,0.55)"
                  fontFamily="'JetBrains Mono', monospace"
                  fontSize="9"
                  letterSpacing="1"
                >
                  {c.city.toUpperCase()}
                </text>
              ))}
            </svg>
            <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-white/40">
              <span>
                Showing <span className="text-white font-bold">{filtered.length}</span> of{" "}
                {ENDPOINTS.length} endpoints across {CITY_STATS.length} cities
              </span>
              <span className="font-mono">DEMO DATA · not live inventory</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 grid md:grid-cols-[240px_1fr] gap-8">
          <aside className="space-y-6" data-testid="network-filters">
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 flex items-center gap-2">
                <Filter size={12} /> Filters
              </div>
              <button
                onClick={clear}
                data-testid="network-clear-filters"
                className="text-xs text-white/50 hover:text-white transition"
              >
                Clear
              </button>
            </div>
            {Object.values(FILTERS).map((f) => (
              <div key={f.key}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">
                  {f.label}
                </div>
                <div className="flex flex-wrap gap-1">
                  {f.options.map((v) => {
                    const on = (filters[f.key] || []).includes(v);
                    return (
                      <button
                        key={v}
                        onClick={() => toggle(f.key, v)}
                        data-testid={`network-filter-${f.key}-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`text-[11px] px-2 py-1 border transition ${
                          on
                            ? "border-[#E8D6A3] text-[#E8D6A3] bg-[#D4B878]/10"
                            : "border-white/10 text-white/50 hover:border-white/30"
                        }`}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </aside>

          <div>
            {selected && (
              <div className="mb-6 p-6 border border-[#E8D6A3]/30 bg-white/[0.02]" data-testid="network-selected">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <div className="font-display font-extrabold text-2xl">{selected.name}</div>
                    <div className="text-xs text-white/50 mt-1">
                      {selected.city} · {selected.country} · {selected.venue_type}
                    </div>
                  </div>
                  <StateBadge state={selected.status === "verified" ? "LIVE" : "BETA"} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                  <KV k="Handle" v={<span className="font-mono break-all text-[#E8D6A3] text-[10px]">{selected.handle}</span>} />
                  <KV k="Media" v={`${selected.media_type} · ${selected.resolution} · ${selected.orientation}`} />
                  <KV k="Audience" v={`${selected.audience_daily.toLocaleString()} / day`} />
                  <KV k="Dwell" v={`${selected.dwell_sec}s avg`} />
                  <KV k="Context" v={selected.context} />
                  <KV k="Environment" v={selected.environment} />
                  <KV k="Indicative CPM" v={`$${selected.cpm}`} accent />
                  <KV k="Proof of Play" v={selected.proof_of_play ? "Enabled" : "Pending"} />
                </div>
                <div className="mt-6">
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black font-semibold text-sm"
                  >
                    Include in a campaign <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="network-grid">
              {filtered.slice(0, 60).map((e) => (
                <button
                  key={e.id}
                  onClick={() => setSelected(e)}
                  data-testid={`network-endpoint-${e.id}`}
                  className={`text-left p-5 border transition ${
                    selected?.id === e.id
                      ? "border-[#E8D6A3] bg-[#D4B878]/5"
                      : "border-white/10 bg-white/[0.02] hover:border-white/30"
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono">
                      {e.id}
                    </span>
                    <span
                      className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border ${
                        e.availability === "booked"
                          ? "border-[#00E5FF]/40 text-[#00E5FF]"
                          : "border-emerald-500/40 text-emerald-400"
                      }`}
                    >
                      {e.availability}
                    </span>
                  </div>
                  <div className="font-display font-extrabold text-lg leading-tight">
                    {e.name}
                  </div>
                  <div className="text-xs text-white/50 mt-1 flex items-center gap-1">
                    <MapPin size={10} /> {e.city} · {e.venue_type}
                  </div>
                  <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-white/40">
                    <span>{e.media_type}</span>
                    <span className="text-[#E8D6A3]">${e.cpm} CPM</span>
                  </div>
                </button>
              ))}
            </div>
            {filtered.length === 0 && (
              <div className="text-center text-white/50 py-20">
                No endpoints match your filters. <button onClick={clear} className="text-[#E8D6A3] underline">Clear</button>
              </div>
            )}
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function KV({ k, v, accent }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">{k}</div>
      <div className={`text-sm ${accent ? "text-[#E8D6A3] font-bold" : "text-white/80"} mt-1`}>
        {v}
      </div>
    </div>
  );
}
